// The stock page's chart (Lightweight Charts), laid out like a trading terminal. Intraday timeframes
// (5 minutes to 4 hours) show earlier sessions on the left and today's session with MoneyHQ's layers;
// scrolling near the oldest candle loads more history. Days / weeks / months show up to eight years with
// the two tested setups drawn on it (new-high breakout with its trailing stop, weekly momentum rank).
// Switching timeframe keeps the window: the same right-edge time and the same number of candles, as on
// TradingView. Timeframe, scale and layers are remembered in this browser; the OHLC readout follows the
// crosshair. The replay control sits in the stock header, and inside the chart only in full screen.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIcon, ChevronDownIcon, Maximize2Icon, Minimize2Icon, RotateCcwIcon, ZoomInIcon, ZoomOutIcon } from 'lucide-react';
import { NSE, PATTERN_RECORD, patternVerdict, type Analysed, type ChartPattern, type Detail } from '@planb/core';
import { DEFAULT_PRO_LAYERS, PRO_LAYERS, ProChart, type ProLayer, type Viewport } from '@/charts/pro-chart';
import { HistoryChart, type Candle } from '@/charts/history-chart';
import { patternLabel } from '@/charts/pattern-layer';
import type { PlanMark } from '@/charts/marks';
import { fmtVol } from '@/charts/stock-chart';
import { useResolvedTheme } from '@/lib/client/use-me';
import { cn } from '@/lib/utils';
import { TurnHint, useFullscreen } from '@/lib/fullscreen';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ZoneEvidence } from './zone-evidence';
import { openDesk } from './desk';
import { actionLabel, type CorpAction } from '@/lib/corp-actions';
import { paperStatus, type PaperRow } from './paper-trades';
import type { TradeDraw } from '@/charts/trade-layer';
import { setupStatus, useChartHistory, useIntradayHistory, useOverlays } from './chart-history';
import { DeskIcon } from './market-ui';

function stored<T extends string>(key: string, all: Set<T>, fallback: Set<T>): Set<T> {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? 'null');
    if (Array.isArray(v)) return new Set(v.filter((x): x is T => all.has(x)));
  } catch { /* unavailable or corrupt: defaults */ }
  return new Set(fallback);
}
function storedOne<T extends string>(key: string, all: readonly T[], fallback: T, legacy: Record<string, T> = {}): T {
  try { const v = JSON.parse(localStorage.getItem(key) ?? 'null'); if (all.includes(v)) return v; if (legacy[v]) return legacy[v]; } catch { /* defaults */ }
  return fallback;
}
const keep = (key: string, v: unknown) => { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* not remembered */ } };

const px = NSE.formatPrice;
const LAYERS_KEY = 'mhq.proLayers';
const VIEWS = ['m5', 'm10', 'm15', 'm30', 'h1', 'h4', 'daily', 'weekly', 'monthly'] as const;
type View = (typeof VIEWS)[number];
// Every timeframe in one table. Intraday: k = 5-minute bars per candle, api = the /v1/candles timeframe.
const TF: Record<View, { label: string; menu: string; intra: boolean; k?: number; api?: string }> = {
  m5: { label: '5m', menu: '5 minutes', intra: true, k: 1, api: '5m' },
  m10: { label: '10m', menu: '10 minutes', intra: true, k: 2, api: '10m' },
  m15: { label: '15m', menu: '15 minutes', intra: true, k: 3, api: '15m' },
  m30: { label: '30m', menu: '30 minutes', intra: true, k: 6, api: '30m' },
  h1: { label: '1h', menu: '1 hour', intra: true, k: 12, api: '1h' },
  h4: { label: '4h', menu: '4 hours', intra: true, k: 48, api: '4h' },
  daily: { label: '1D', menu: '1 day', intra: false },
  weekly: { label: '1W', menu: '1 week', intra: false },
  monthly: { label: '1M', menu: '1 month', intra: false },
};
const VIEW_GROUPS: { label: string; views: View[]; note?: string }[] = [
  { label: 'Minutes', views: ['m5', 'm10', 'm15', 'm30'], note: '1, 2 and 3 minutes need 1-minute data, which MoneyHQ doesn’t store' },
  { label: 'Hours', views: ['h1', 'h4'] },
  { label: 'Days · with tested setups', views: ['daily', 'weekly', 'monthly'] },
];
// Intraday history loads in steps as you scroll back (5-minute data goes back a year).
const DEPTHS = ['1m', '3m', '1y'] as const;
const startDepth = (v: View) => (v === 'h1' || v === 'h4' ? 1 : 0);
// Range buttons, as on TradingView: each picks a timeframe that fits the range on screen (the chart can
// draw about 1,400 candles across its width, so a year of 5-minute candles isn't a usable view).
const RANGE_BUTTONS: { label: string; view: View; sessions?: number; candles?: number }[] = [
  { label: '1d', view: 'm5', sessions: 1 },
  { label: '5d', view: 'm5', sessions: 5 },
  { label: '1m', view: 'm30', sessions: 21 },
  { label: '3m', view: 'h1', sessions: 63 },
  { label: '1y', view: 'daily', candles: 250 },
  { label: '5y', view: 'weekly', candles: 260 },
];
/** What the desk agent reads and does on this chart (see lib/desk/tools). */
export interface ChartDesk {
  state(): Record<string, unknown>;
  /** Switch timeframe by its label (5m … 1M); false for an unknown label. */
  switchTo(label: string): boolean;
  /** This timeframe's candles (and patterns) are in. */
  ready(): boolean;
  mark(levels: PlanMark[]): void;
}
export const TIMEFRAMES = VIEWS.map((v) => TF[v].label);

const depthFor = (sessions: number) => (sessions <= 21 ? 0 : sessions <= 50 ? 1 : 2); // 3 calendar months hold under 63 sessions

export function IntradayChart({ a, d, t, symbol, date, prev, replay, live, legend, deskRef, paper, actions }: {
  a: Analysed; d: Detail; t: number; symbol: string; date: string;
  prev?: { high?: number; low?: number; close: number };
  /** Shown in full screen, where the stock header isn't visible. */
  replay: React.ReactNode;
  /** Real market data (practice mode has no stored history: intraday shows today only, days are off). */
  live: boolean;
  /** The key to today's layers, shown under the chart on intraday timeframes. */
  legend?: React.ReactNode;
  /** Filled with this chart's handle for the desk agent. */
  deskRef?: React.MutableRefObject<ChartDesk | null>;
  /** The user's paper trades in this stock (filled ones are drawn). */
  paper?: PaperRow[];
  /** The stock's corporate actions: marked at their ex-dates on the days chart. */
  actions?: CorpAction[];
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const histRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ProChart | null>(null);
  // Full screen, landscape on phones (lib/fullscreen.ts); the chart refits to the new size.
  const full = useFullscreen<HTMLDivElement>(() => chartRef.current?.refitAfterResize());
  const fsRef = full.ref, fs = full.fs, toggleFs = full.toggle;
  const histChart = useRef<HistoryChart | null>(null);
  const [layers, setLayers] = useState(() => stored(LAYERS_KEY, new Set(PRO_LAYERS.map((l) => l.id)), DEFAULT_PRO_LAYERS));
  const [view0, setView] = useState<View>(() => storedOne('mhq.chartView', VIEWS, 'm5', { today5: 'm5', today15: 'm15' }));
  const [showPatterns, setShowPatterns] = useState(() => storedOne('mhq.chartPatterns', ['on', 'off'] as const, 'on') === 'on');
  const [scale, setScale] = useState(() => storedOne('mhq.chartScale', ['normal', 'log', 'pct'] as const, 'normal'));
  const [depth, setDepth] = useState<Partial<Record<View, number>>>({});
  const [, setPatternTick] = useState(0);
  const [hover, setHover] = useState<number | null>(null);
  const [hHover, setHHover] = useState<number | null>(null);
  const [picked, setPicked] = useState<number | null>(null); // source candle of the zone clicked
  const theme = useResolvedTheme();
  const view: View = !live && !TF[view0].intra ? 'm5' : view0;
  const intra = TF[view].intra;
  const k = TF[view].k ?? 1;
  const dIdx = depth[view] ?? startDepth(view);
  const pendingVP = useRef<Viewport | null>(null);
  const pendingDays = useRef<number | null>(null);
  useEffect(() => { setDepth({}); }, [symbol]);

  useEffect(() => {
    if (!boxRef.current || !histRef.current) return;
    const c = new ProChart(boxRef.current);
    chartRef.current = c;
    c.onZone = (z) => setPicked(z ? z.idx : null);
    c.onNeedMore = () => needMore.current();
    c.onPatterns = () => setPatternTick((n) => n + 1); // re-render the list under the chart
    const onMove = (p: { logical?: number }) => setHover(p.logical == null ? null : Math.round(p.logical));
    c.chart.subscribeCrosshairMove(onMove);
    const h = new HistoryChart(histRef.current);
    histChart.current = h;
    const onHMove = (p: { logical?: number }) => setHHover(p.logical == null ? null : Math.round(p.logical));
    h.chart.subscribeCrosshairMove(onHMove);
    if (import.meta.env.DEV) (window as unknown as { __mhqChart: unknown }).__mhqChart = { pro: c, hist: h };
    return () => {
      c.chart.unsubscribeCrosshairMove(onMove); c.destroy(); chartRef.current = null;
      h.chart.unsubscribeCrosshairMove(onHMove); h.destroy(); histChart.current = null;
    };
  }, []);
  useEffect(() => { chartRef.current?.applyTheme(); histChart.current?.applyTheme(); }, [theme]);
  useEffect(() => { histChart.current?.setScale(scale); chartRef.current?.setScale(scale); }, [scale]);

  // ---- Intraday: earlier sessions at this timeframe + today's session with MoneyHQ's layers.
  const api = TF[view].api ?? '5m';
  const ih = useIntradayHistory(symbol, api, DEPTHS[dIdx], live && intra, intra ? Math.floor(t / 12) : 0);
  const pastReady = ih.key === `${symbol}|${api}`;
  const past = live && pastReady ? ih.candles : undefined;
  // Loaded = this timeframe's candles, at the history depth asked for (a longer depth may still be on its way).
  const pastLoaded = !live || (pastReady && !ih.loading && ih.range === DEPTHS[dIdx]);
  // Load more history when the view nears the oldest candle.
  const needMore = useRef(() => {});
  needMore.current = () => { if (intra && live && !ih.loading && dIdx < DEPTHS.length - 1) setDepth((x) => ({ ...x, [view]: dIdx + 1 })); };
  // Patterns start at a fixed date inside the first history step loaded (1 month; 3 for hours), so the
  // same candles always give the same patterns, however far back the chart has been scrolled.
  const patternsFrom = Date.parse(`${date}T00:00:00Z`) / 1000 - (startDepth(view) ? 85 : 27) * 86400;
  useEffect(() => {
    chartRef.current?.update({ a, d, t, market: NSE, date, prev, layers, k, past, pastLoaded, patterns: showPatterns && intra, patternsFrom });
  }, [a, d, t, date, prev, layers, k, past, pastLoaded, showPatterns, intra, patternsFrom]);

  // ---- Days / weeks / months, with the tested setups.
  const days = !intra;
  const hist = useChartHistory(symbol, live, days ? t : 0); // also on intraday views: the desk reads the daily setups
  const dayKind = view === 'monthly' ? 'monthly' : view === 'weekly' ? 'weekly' : 'daily';
  const overlays0 = useOverlays(hist, dayKind, showPatterns);
  // Holding a paper trade by the breakout rule in this stock: draw only that trade's stop, not the
  // overlay's (same rule, slightly different starting stop: two lines would confuse).
  const holdsTrailing = (paper ?? []).some((r) => r.trade.trail && r.state.fill != null && !r.state.final);
  const overlays = useMemo(() => (holdsTrailing ? { ...overlays0, stop: overlays0.stop.map((p) => ({ ...p, v: null })) } : overlays0), [overlays0, holdsTrailing]);
  const candles: Candle[] = view === 'monthly' ? hist.monthly : view === 'weekly' ? hist.weekly : hist.daily;
  const shownKey = useRef('');
  useEffect(() => {
    const h = histChart.current;
    if (!h || !days || !candles.length) return;
    h.setData(candles, false, false);
    h.setOverlays(overlays);
    const key = `${symbol}|${view}`;
    if (shownKey.current !== key) {
      shownKey.current = key;
      if (pendingDays.current) { h.showLast(pendingDays.current); pendingDays.current = null; }
      else if (pendingVP.current) { h.setViewport(pendingVP.current); pendingVP.current = null; }
      else h.showLast(dayKind === 'monthly' ? 60 : dayKind === 'weekly' ? 104 : 250);
    }
  }, [candles, overlays, days, symbol, view, dayKind]);

  // ---- Switching timeframe keeps the window (TradingView-style): the same right-edge time and the
  // same number of candles; at the live end it stays at the live end.
  const switchTo = (v: View) => {
    if (v === view) return;
    const vp = (intra ? chartRef.current?.getViewport() : histChart.current?.getViewport()) ?? null;
    chartRef.current?.requestSessions(null);
    if (TF[v].intra && vp && !vp.atEnd) {
      // Load the new timeframe deep enough to cover the moment being looked at.
      const daysBack = (Date.parse(`${date}T00:00:00Z`) / 1000 - vp.right) / 86400;
      const need = daysBack <= 28 ? 0 : daysBack <= 88 ? 1 : 2;
      setDepth((x) => ({ ...x, [v]: Math.max(x[v] ?? startDepth(v), need) }));
    }
    if (TF[v].intra) {
      // The analysis chart applies it once this timeframe's history is in.
      chartRef.current?.requestViewport(vp);
      if (TF[v].k === k && !intra && pastLoaded) { if (vp) chartRef.current?.setViewport(vp); chartRef.current?.requestViewport(null); }
    } else {
      pendingVP.current = vp;
      shownKey.current = ''; // days chart: apply the window on its next draw
    }
    setView(v); keep('mhq.chartView', v);
  };


  // OHLC readout: the candle under the crosshair, else the latest.
  const ro = intra ? chartRef.current?.readout(hover) ?? {} : {};
  const th = ro.today;
  const i = th != null && th >= 0 && th * k < d.bars.length ? th * k : d.bars.length - 1;
  const bk = d.bars.slice(Math.floor(i / k) * k, Math.floor(i / k) * k + k); // today's candle at this size
  const b = bk.length ? { o: bk[0].o, h: Math.max(...bk.map((x) => x.h)), l: Math.min(...bk.map((x) => x.l)), c: bk[bk.length - 1].c, v: bk.reduce((s, x) => s + x.v, 0), flow: bk.reduce((s, x) => s + (x.bv || 0) - (x.sv || 0), 0), measured: bk.some((x) => (x.bv || 0) + (x.sv || 0) > 0) } : null;
  const hc = days ? histChart.current?.candleAt(hHover) : ro.past;
  const status = days ? setupStatus(hist) : null;
  // The patterns to name under the chart: those still forming, and breakouts in the last 30 candles.
  const found: { p: ChartPattern; breakT: number | null }[] = !showPatterns || view === 'monthly' ? []
    : days ? hist.patterns.map((p) => ({ p, breakT: p.breakIdx === null ? null : hist.daily[p.breakIdx].t }))
    : (chartRef.current?.patternsFound ?? []).map((p) => ({ p, breakT: p.breakT }));
  const foundN = days ? hist.daily.length : chartRef.current?.patternsN ?? 0;
  const recentPatterns = found.filter((x) => x.p.breakIdx === null || x.p.breakIdx >= foundN - 30).slice(-3).reverse();
  // The same swing points can fit a bullish and a bearish shape at once (rising lows under a near-flat
  // top are both an ascending triangle and a rising wedge): that is a squeeze, decided by the first close
  // out of it, so it reads as one two-way line.
  const formingUp = recentPatterns.find((x) => x.breakT === null && x.p.bull), formingDown = recentPatterns.find((x) => x.breakT === null && !x.p.bull);
  const twoWay = formingUp && formingDown ? { up: formingUp.p, down: formingDown.p } : null;
  const fmtT = (sec: number, withTime: boolean) => new Date(sec * 1000).toLocaleString('en-IN', withTime ? { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' } : { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

  // ---- The user's paper trades in this stock, on whichever chart is showing (times snapped to it).
  const filled = (paper ?? []).filter((r) => r.state.fill != null && r.state.fillT != null);
  const drawnKey = `${view}|${candles.length}|${ih.candles.length}|${ih.key}|${JSON.stringify(filled.map((r) => [r.id, r.state.status, r.state.lastT, r.state.stopNow]))}`;
  useEffect(() => {
    const target = intra ? chartRef.current : histChart.current;
    if (!target) return;
    const snap = (sec: number) => target.snapTime(sec);
    const dayStart = (date: string) => Date.parse(`${date}T09:15:00Z`) / 1000;
    const exitLabel: Record<string, string> = { target: 'Target', stop: 'Stop', 'squared-off': 'Squared off', closed: 'Closed', time: 'Held 20 sessions' };
    const draws: TradeDraw[] = [];
    for (const r of filled) {
      const s = r.state, tr = r.trade;
      const et = snap(s.fillT!);
      if (et == null) continue;
      const endT = snap(s.exitT ?? s.lastT) ?? et;
      // A level as drawn: as placed, then each edit from the 5-minute candle after it (never backwards).
      const steps = (start: number, key: 'stop' | 'target') => {
        const pts = [{ t: et, v: start }];
        let v = start;
        for (const c of tr.changes ?? []) {
          const nv = c[key];
          const at = nv == null ? null : snap(Math.floor(c.at / 300) * 300 + 300);
          if (nv == null || at == null || at > endT) continue;
          const t = Math.max(at, et);
          pts.push({ t, v }, { t, v: nv });
          v = nv;
        }
        pts.push({ t: endT, v });
        return pts;
      };
      const stop = tr.trail === 'results' ? [] // the results-drift rule has no stop (a 20-session hold)
        : tr.trail && s.stopPath?.length
          ? s.stopPath.map((p) => ({ t: snap(dayStart(p.date)), v: p.stop })).filter((p): p is { t: number; v: number } => p.t != null && p.v > 0)
          : steps(tr.stop, 'stop');
      // An open trailing trade ends at the stop after the latest close (where it stands now).
      if (tr.trail && !s.final && s.stopNow && stop.length) stop.push({ t: stop[stop.length - 1].t, v: s.stopNow });
      draws.push({
        id: r.id, side: tr.side, entry: { t: et, price: s.fill! },
        exit: s.exit != null && s.exitT != null ? { t: snap(s.exitT) ?? endT, price: s.exit, label: exitLabel[s.status] ?? 'Exit' } : undefined,
        stop, target: tr.target > 0 ? steps(tr.target, 'target') : undefined, label: r.setup,
      });
    }
    target.setTrades(draws);
    (intra ? histChart.current : chartRef.current)?.setTrades([]);
  }, [drawnKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Corporate actions at their ex-dates on the days chart (the prices are adjusted for splits and
  // bonuses, so a split shows as a label, not a fall).
  const actionsKey = `${view}|${ih.key}|${ih.candles.length}|${hist.daily.length}|${candles.length}|${(actions ?? []).map((x) => x.exDate + x.kind).join(',')}`;
  useEffect(() => {
    const h = histChart.current;
    if (!h) return;
    if (intra) { h.setEvents([]); return; }
    const ev: { t: number; text: string }[] = [];
    for (const x of actions ?? []) {
      const text = actionLabel(x), at = h.snapTime(Date.parse(`${x.exDate}T09:15:00Z`) / 1000);
      // Only on a candle of the ex-date itself (snapTime gives the candle at or before the time).
      if (text && at != null && new Date(at * 1000).toISOString().slice(0, 10) === x.exDate) ev.push({ t: at, text });
    }
    h.setEvents(ev);
  }, [actionsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- The desk agent's view of this chart (refreshed every render, so it is always current).
  if (deskRef) {
    const viewOf = (label: string) => VIEWS.find((v) => TF[v].label.toLowerCase() === label.trim().toLowerCase());
    deskRef.current = {
      state: () => {
        const vp = (intra ? chartRef.current?.getViewport() : histChart.current?.getViewport()) ?? null;
        const shown = (sec: number) => fmtT(sec, intra);
        const pat = ({ p, breakT }: { p: ChartPattern; breakT: number | null }) => ({
          pattern: p.name, direction: p.bull ? 'bullish' : 'bearish',
          status: breakT === null ? 'forming' : `broke ${p.bull ? 'above' : 'below'} its level on ${shown(breakT)}`,
          level: +p.level.to.p.toFixed(2), stop: +p.stop.toFixed(2), measuredMoveTarget: +p.target.toFixed(2),
          testedOnDailyCandles: patternVerdict(p.name), testedEdge: PATTERN_RECORD[p.name].edge,
        });
        const daily = setupStatus(hist);
        const lastB = hist.breakouts[hist.breakouts.length - 1];
        return {
          timeframe: TF[view].label,
          visibleWindow: vp ? { endsAt: shown(vp.right), candles: Math.round(vp.bars), atLatest: vp.atEnd } : null,
          latestCandle: hc ? { time: shown(hc.t), o: hc.o, h: hc.h, l: hc.l, c: hc.c, v: hc.v } : b ? { time: NSE.timeLabel(Math.floor(i / k) * k), o: b.o, h: b.h, l: b.l, c: b.c, v: b.v } : null,
          chartPatterns: !showPatterns ? 'switched off by the user (Indicators menu)' : view === 'monthly' ? 'not detected on monthly candles' : {
            note: intra ? `Found on ${TF[view].label} candles from ${fmtT(patternsFrom, false)}; MoneyHQ tested them on daily candles only, so on this timeframe they are untested.` : 'Found on daily candles, the timeframe MoneyHQ tested.',
            twoWaySqueeze: twoWay ? `A bullish ${twoWay.up.name} (close above ${twoWay.up.level.to.p.toFixed(2)}) and a bearish ${twoWay.down.name} (close below ${twoWay.down.level.to.p.toFixed(2)}) are forming together: the first close out of that range decides.` : null,
            // Both sides of a squeeze as conditional breakout plans (the desk plans both, never one).
            squeezePlans: twoWay ? [twoWay.up, twoWay.down].map((p) => ({
              side: p.bull ? 'long' : 'short', entry_type: 'stop', entry: +p.level.to.p.toFixed(2), stop: +p.stop.toFixed(2), target: +p.target.toFixed(2),
              setup: `${p.name} ${p.bull ? 'breakout' : 'breakdown'} (${TF[view].label})`, tested: `on daily candles: ${patternVerdict(p.name)}; untested on ${TF[view].label}`,
            })) : null,
            recent: recentPatterns.map(pat),
            earlierOnChart: Math.max(0, found.length - recentPatterns.length),
          },
          testedDailySetups: live ? {
            newHighBreakout: daily.breakout || 'no data',
            newHighBreakoutRule: 'Close at a 52-week and all-time high, enter at the next open, trailing stop 10 × ATR(20) under the highest close. MoneyHQ test (NSE 500, 2019-2026, after costs): beat a random-stock placebo by about 5% a trade, held about 6 months.',
            openBreakout: lastB && lastB.exitIdx === null ? { since: fmtT(hist.daily[lastB.entryIdx].t, false), entry: lastB.entry, trailingStop: +lastB.stopNow.toFixed(2) } : null,
            momentum: daily.momentum || 'no data',
            highs: (() => {
              const dd = hist.daily, last = dd[dd.length - 1];
              if (!last) return null;
              const yr = dd.slice(-252), hi52 = Math.max(...yr.map((x) => x.h)), lo52 = Math.min(...yr.map((x) => x.l)), ath = Math.max(...dd.map((x) => x.h));
              const pct = (x: number) => +(((last.c - x) / x) * 100).toFixed(2);
              return { high52w: hi52, low52w: lo52, allTimeHighInData: ath, closeVs52wHighPct: pct(hi52), closeVsAllTimeHighPct: pct(ath), dataFrom: fmtT(dd[0].t, false) };
            })(),
          } : 'practice mode: no stored history',
        };
      },
      switchTo: (label) => { const v = viewOf(label); if (!v || (!live && !TF[v].intra)) return false; switchTo(v); return true; },
      ready: () => (intra ? pastLoaded && (!showPatterns || (chartRef.current?.patternsN ?? 0) > 0 || !live) : !hist.loading && candles.length > 0),
      mark: (levels) => { chartRef.current?.setMarks(levels); histChart.current?.setMarks(levels); },
    };
  }

  const iconBtn = 'size-6.5 rounded-full [&_svg]:size-3.5';
  const ch = days ? histChart.current : chartRef.current;
  const fitView = () => (days ? histChart.current?.showLast(dayKind === 'monthly' ? 60 : dayKind === 'weekly' ? 104 : 250) : chartRef.current?.reset());
  const onRange = (r: (typeof RANGE_BUTTONS)[number]) => {
    if (r.sessions) {
      const need = depthFor(r.sessions);
      chartRef.current?.requestViewport(null);
      if (r.view === view && (!live || dIdx >= need)) { chartRef.current?.requestSessions(null); chartRef.current?.showSessions(r.sessions); return; }
      chartRef.current?.requestSessions(r.sessions); // applied by the chart once the data is in
      if (live) setDepth((x) => ({ ...x, [r.view]: Math.max(x[r.view] ?? startDepth(r.view), need) }));
    } else {
      if (r.view === view) { histChart.current?.showLast(r.candles!); return; }
      pendingDays.current = r.candles!; pendingVP.current = null; shownKey.current = '';
    }
    setView(r.view); keep('mhq.chartView', r.view);
  };
  return (
    <div ref={fsRef} className={cn('bg-card', fs && 'flex h-dvh flex-col p-4', full.cover)}>
      {full.turnHint && <TurnHint />}
      {fs && (
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <span className="font-heading text-lg font-semibold text-ink">{symbol}</span>
          {intra && replay}
        </div>
      )}
      {/* Top bar, as on a trading terminal: symbol, timeframe and indicators on the left; view tools on the right. */}
      <div className="flex h-10 items-center justify-between gap-2 border-b px-2">
        <div className="flex min-w-0 items-center gap-1">
          {/* The symbol and timeframe also read in the chart's legend line below, as on TradingView. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-[12px] font-semibold" aria-label="Timeframe">
                {TF[view].label} <ChevronDownIcon className="size-3.5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-[70vh] w-56 overflow-y-auto">
              <DropdownMenuRadioGroup value={view} onValueChange={(v) => switchTo(v as View)}>
                {VIEW_GROUPS.map((g, gi) => (
                  <Fragment key={g.label}>
                    {gi > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuLabel className="text-[11px] tracking-wide text-muted-foreground uppercase">{g.label}</DropdownMenuLabel>
                    {g.note && ['1 minute', '2 minutes', '3 minutes'].map((m) => (
                      <DropdownMenuItem key={m} disabled className="text-[13px]" title={g.note}>{m}<span className="ml-auto text-[10px] text-muted-foreground">no data</span></DropdownMenuItem>
                    ))}
                    {g.views.map((v) => {
                      const off = !live && !TF[v].intra;
                      return (
                        <DropdownMenuRadioItem key={v} value={v} disabled={off} className="text-[13px]" title={off ? 'Needs real market data (not available in practice mode)' : undefined}>
                          {TF[v].menu}
                        </DropdownMenuRadioItem>
                      );
                    })}
                  </Fragment>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="h-4 w-px bg-border" aria-hidden />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-[12px] font-medium" aria-label="Indicators">
                <ActivityIcon className="size-3.5" /> Indicators
                {intra && <span className="num rounded bg-muted px-1 text-[10px] text-muted-foreground">{layers.size + (showPatterns ? 1 : 0)}</span>}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuLabel className="text-[11px] tracking-wide text-muted-foreground uppercase">Chart patterns · every candle shown</DropdownMenuLabel>
              <DropdownMenuCheckboxItem checked={showPatterns} className="text-[13px]" onSelect={(e) => e.preventDefault()}
                onCheckedChange={(on) => { setShowPatterns(on); keep('mhq.chartPatterns', on ? 'on' : 'off'); }}>
                Double bottoms, flags, wedges…
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              {intra ? (<>
                <DropdownMenuLabel className="text-[11px] tracking-wide text-muted-foreground uppercase">MoneyHQ layers · today’s session</DropdownMenuLabel>
                {PRO_LAYERS.map((l) => (
                  <DropdownMenuCheckboxItem key={l.id} checked={layers.has(l.id)} className="text-[13px]" onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(on) => { const n = new Set(layers); if (on) n.add(l.id); else n.delete(l.id); setLayers(n); keep(LAYERS_KEY, [...n]); }}>
                    {l.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </>) : (
                <p className="px-2 py-1.5 text-[12px] text-muted-foreground">Drawn on this view: new-high breakouts with their trailing stop, and the weekly momentum rank (the two setups MoneyHQ tested). MoneyHQ’s session layers are on the minute and hour timeframes.</p>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <Button variant="ghost" size="sm" className="mr-1 h-7 gap-1.5 px-2 text-[12px] font-medium text-brand-strong" title="Ask the desk about this chart (⌘J)"
            onClick={() => openDesk('What pattern is this chart forming?')}>
            <DeskIcon className="size-3.5" /> Ask
          </Button>
          <Button variant="ghost" size="icon" className={cn(iconBtn, 'max-sm:hidden')} aria-label="Zoom in" title="Zoom in" onClick={() => ch?.zoom(1 / 1.5)}><ZoomInIcon /></Button>
          <Button variant="ghost" size="icon" className={cn(iconBtn, 'max-sm:hidden')} aria-label="Zoom out" title="Zoom out" onClick={() => ch?.zoom(1.5)}><ZoomOutIcon /></Button>
          <Button variant="ghost" size="icon" className={iconBtn} aria-label={intra ? 'Show today’s session' : 'Reset the view'} title={intra ? 'Show today’s session' : 'Reset the view'} onClick={fitView}><RotateCcwIcon /></Button>
          <Button variant="ghost" size="icon" className={iconBtn} aria-label={fs ? 'Exit full screen' : 'Full screen'} title={fs ? 'Exit full screen (Esc)' : 'Full screen'} onClick={toggleFs}>
            {fs ? <Minimize2Icon /> : <Maximize2Icon />}
          </Button>
        </div>
      </div>
      <div className={cn('relative', fs ? 'min-h-0 flex-1' : 'h-[520px] max-sm:h-[max(320px,calc(100svh-24rem))]')}>
        {hc ? (
          <div className="num pointer-events-none absolute top-2 left-3 z-10 flex flex-wrap gap-x-3 text-[12px] text-muted-foreground">
            <span className="font-semibold text-ink">{symbol} · {TF[view].label} · NSE</span>
            <span>{fmtT(hc.t, intra)}</span>
            {(['o', 'h', 'l', 'c'] as const).map((key) => (
              <span key={key}>{key.toUpperCase()} <b className={cn('font-medium', hc.c >= hc.o ? 'text-bull' : 'text-bear')}>{px(hc[key])}</b></span>
            ))}
            <span>Vol <b className="font-medium text-ink">{fmtVol(hc.v)}</b></span>
          </div>
        ) : intra && b && (
          <div className="num pointer-events-none absolute top-2 left-3 z-10 flex flex-wrap gap-x-3 text-[12px] text-muted-foreground">
            <span className="font-semibold text-ink">{symbol} · {TF[view].label} · NSE</span>
            <span>{NSE.timeLabel(Math.floor(i / k) * k)}</span>
            {(['o', 'h', 'l', 'c'] as const).map((key) => (
              <span key={key}>{key.toUpperCase()} <b className={cn('font-medium', b.c >= b.o ? 'text-bull' : 'text-bear')}>{px(b[key])}</b></span>
            ))}
            <span>Vol <b className="font-medium text-ink">{fmtVol(b.v)}</b></span>
            {b.measured && <span>Δ <b className={cn('font-medium', b.flow >= 0 ? 'text-bull' : 'text-bear')}>{b.flow >= 0 ? '+' : '−'}{fmtVol(Math.abs(b.flow))}</b></span>}
          </div>
        )}
        {days && !hist.loading && (hist.error || !candles.length) && (
          <p className="absolute inset-0 z-10 grid place-items-center text-sm text-muted-foreground">{hist.error ? 'History could not be loaded. Try again in a moment.' : 'No stored history for this stock yet.'}</p>
        )}
        {intra && picked != null && (() => {
          const z = a.obs.find((o) => o.idx === picked);
          return z ? <ZoneEvidence o={z} a={a} onClose={() => setPicked(null)} /> : null;
        })()}
        <div ref={boxRef} className={cn('absolute inset-0', days && 'invisible')} role="img" aria-label={`${symbol} ${TF[view].menu} candles`} />
        <div ref={histRef} className={cn('absolute inset-0', intra && 'invisible')} role="img" aria-label={`${symbol} ${TF[view].menu} candles with MoneyHQ's tested setups`} />
        {intra && live && ih.loading && pastReady && <span className="pointer-events-none absolute bottom-2 left-3 z-10 text-[11px] text-muted-foreground">Loading earlier sessions…</span>}
      </div>
      {/* Bottom bar: date ranges on the left, price scale on the right. */}
      <div className="flex h-9 items-center justify-between gap-2 border-t px-2">
        <div className="flex items-center gap-0.5" role="group" aria-label="Range">
          {RANGE_BUTTONS.filter((r) => live || r.label === '1d').map((r) => (
            <Button key={r.label} variant="ghost" size="sm" className="h-6.5 px-2 text-[12px] text-muted-foreground hover:text-ink"
              title={`${r.label === '1d' ? 'Today' : `The last ${r.label}`} on ${TF[r.view].menu} candles`} onClick={() => onRange(r)}>{r.label}</Button>
          ))}
        </div>
        <div className="flex items-center gap-0.5" role="group" aria-label="Price scale">
          {(['pct', 'log'] as const).map((m) => (
            <Button key={m} variant="ghost" size="sm" aria-pressed={scale === m} title={m === 'pct' ? 'Percent scale (from the first visible candle)' : 'Logarithmic scale'}
              className={cn('h-6.5 px-2 text-[12px]', scale === m ? 'bg-brand-soft font-semibold text-brand-strong hover:bg-brand-soft' : 'text-muted-foreground')}
              onClick={() => { const n = scale === m ? 'normal' : m; setScale(n); keep('mhq.chartScale', n); }}>{m === 'pct' ? '%' : 'log'}</Button>
          ))}
        </div>
      </div>
      {/* The key to what's drawn, and the TradingView credit its licence asks for, in every view. */}
      {/* Full screen on a short (landscape phone) screen: the chart gets the height; the notes stay on the page. */}
      <div className={cn('flex items-end gap-3 border-t px-4 py-2.5 text-[11.5px] text-muted-foreground', fs && '[@media(max-height:520px)]:hidden')}>
        <div className="min-w-0 flex-1 space-y-1">
          {showPatterns && view !== 'monthly' && (live || intra) && (
            <div className="space-y-0.5">
              {twoWay && (
                <p>
                  <b className="text-ink">Two-way squeeze</b>: the latest swing points fit both a bullish {patternLabel(twoWay.up.name).toLowerCase()} (breaks out on a close above {px(twoWay.up.level.to.p)}) and a bearish {patternLabel(twoWay.down.name).toLowerCase()} (on a close below {px(twoWay.down.level.to.p)}).
                  {' '}Neither says which way: the first close out of that range decides. Tested on daily candles, the {patternLabel(twoWay.up.name).toLowerCase()}: {patternVerdict(twoWay.up.name)}; the {patternLabel(twoWay.down.name).toLowerCase()}: {patternVerdict(twoWay.down.name)}.
                </p>
              )}
              {recentPatterns.length ? recentPatterns.filter((x) => !twoWay || x.breakT !== null).map(({ p, breakT }) => (
                <p key={`${p.name}${p.points[0].i}`}>
                  <b className="text-ink">{patternLabel(p.name)}</b>
                  {breakT === null
                    ? <> forming · breaks out on a close {p.bull ? 'above' : 'below'} {px(p.level.to.p)}</>
                    : <> · broke {p.bull ? 'above' : 'below'} {px(p.level.to.p)} on {fmtT(breakT, intra)}</>}
                  {' '}· tested on daily candles: {patternVerdict(p.name)}
                </p>
              )) : <p><b className="text-ink">Chart patterns</b>: none forming or broken out in the last 30 candles{found.length ? ' · earlier ones are marked ● on the chart' : ''}</p>}
              <p>Found with the exact rules MoneyHQ tested (swing points 2 × ATR apart, 12 classic shapes; thin line: the shape, dashed: its breakout level){intra ? `; on ${TF[view].label} candles they are untested, and are found from ${fmtT(patternsFrom, false)} on` : ''}. Most did no better than random dates. For learning, not advice.</p>
            </div>
          )}
          {filled.map((r) => {
            const s = r.state, tr = r.trade, st = paperStatus(s.status);
            const money = `${s.net >= 0 ? '+' : '−'}₹${Math.abs(s.net).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
            return (
              <p key={r.id} className="flex flex-wrap items-center gap-x-2">
                <b className="text-ink">Paper {tr.side} {tr.qty.toLocaleString('en-IN')}</b> @ {px(s.fill!)}
                <span className={cn('rounded-full px-1.5 text-[10.5px] font-semibold', st.cls)}>{st.label}</span>
                {s.exit != null ? <>exit {px(s.exit)}</> : tr.trail ? <>trailing stop {px(s.stopNow || tr.stop)}</> : <>stop {px(tr.stop)} · target {px(tr.target)}</>}
                <span className={cn('num font-medium', s.net >= 0 ? 'text-bull' : 'text-bear')}>{money}{!s.final && ' now'} ({s.r >= 0 ? '+' : ''}{s.r.toFixed(2)}R)</span>
                <button type="button" className="text-brand-strong hover:underline" onClick={() => openDesk(`Review my ${tr.symbol} paper trade (ref ${r.id.slice(0, 8)})`)}>Review</button>
              </p>
            );
          })}
          {intra && legend && <p className="flex flex-wrap items-center gap-x-2 gap-y-1">{legend}</p>}
          {days && status && (<>
            <p className="flex flex-wrap gap-x-3 gap-y-1">
              {status.breakout && <span><b className="text-ink">New-high breakout</b>: {status.breakout}</span>}
              {status.momentum && <span><b className="text-ink">Momentum</b>: {status.momentum}</span>}
            </p>
            <p>
              <span className="text-bull">▲ New high</span>: a close at a 52-week and all-time high (buy the next open) · <span className="text-amber-600 dark:text-amber-400">dashed line</span>: the trailing stop, 10 × ATR(20) under the highest close · bottom pane: weekly momentum rank among today’s NSE 500 (NSE-style 6/12-month score).
              Tested by MoneyHQ on today’s NSE 500, Nov 2019 – Oct 2026, after costs: these breakouts beat a random-stock placebo by about 5% a trade (held about 6 months); the momentum top 20 beat an equal-weight basket by about 8 points a year. Past results on today’s list are optimistic; for learning, not advice.
            </p>
          </>)}
        </div>
        <a className="shrink-0 text-muted-foreground/70 hover:text-ink" href="https://www.tradingview.com/" target="_blank" rel="noopener"
          title="Chart by TradingView Lightweight Charts™" aria-label="Chart by TradingView Lightweight Charts™">
          <svg viewBox="0 0 35 19" className="h-2.5 w-auto" fill="currentColor" aria-hidden="true"><path d="M14 2H2v6h6v9h6V2Zm12 15h-7l6-15h7l-6 15Zm-7-9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" /></svg>
        </a>
      </div>
    </div>
  );
}
